import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useOrg } from '@/admin/hooks/use-org'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useSession } from '@/admin/hooks/use-session'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useCreateTableView,
  useDeleteTableView,
  useSetOrgDefaultView,
  useTableViews,
  useUpdateTableView,
} from '@/admin/hooks/use-table-views'
import type { TableView, TableViewConfig } from '@/admin/types/table-views'

/**
 * One part of a list's state that a saved view can hold: the sort, the
 * Filters menu, the label filter, the status pill, the time range, the scope.
 * The page keeps owning the state; the slice tells the hook how to read it,
 * show a value, and (for user preferences) save or restore it.
 */
export interface ListStateSlice<T> {
  value: T
  /** Show a value on screen. Never persists anything. */
  apply: (value: T) => void
  /** The list's resting value. A view saved before this slice existed opens with it. */
  empty: T
  /** Save as the user's own preference. Used only while no view is open. */
  savePreference?: (value: T) => void
  /** Back to the user's own preference when a view closes. Defaults to `apply(empty)`. */
  restorePreference?: () => void
  /**
   * Navigation rather than a filter (the All / My Claims tabs). A view keeps
   * the value it was saved with, a view without one leaves the list where it
   * is, and changing it by hand closes a view saved with another value
   * instead of rewriting that view.
   */
  navigation?: boolean
}

export interface ListStateSlices {
  sorting?: ListStateSlice<NonNullable<TableViewConfig['sorting']> | null>
  filters?: ListStateSlice<NonNullable<TableViewConfig['filters']>>
  filterValues?: ListStateSlice<NonNullable<TableViewConfig['filterValues']>>
  labels?: ListStateSlice<string[]>
  status?: ListStateSlice<string | null>
  timeRange?: ListStateSlice<NonNullable<TableViewConfig['timeRange']>>
  scope?: ListStateSlice<string>
}

export type ListStateSliceKey = keyof ListStateSlices
type SliceValue<K extends ListStateSliceKey> = NonNullable<ListStateSlices[K]>['value']

// The order a view is put on screen. Scope goes last: the tab writes the
// address bar after the sort has.
const SLICE_KEYS: ListStateSliceKey[] = [
  'sorting',
  'filters',
  'filterValues',
  'labels',
  'status',
  'timeRange',
  'scope',
]

interface ViewColumn {
  id: string
  defaultVisible?: boolean
}

export interface UseSavedListViewOptions {
  tableId: string
  /** The table's columns as currently known. More may arrive later. */
  columns: ViewColumn[]
  columnVisibility: Record<string, boolean>
  setColumnVisibility: (visibility: Record<string, boolean>) => void
  saveColumnVisibility: (visibility: Record<string, boolean>) => void
  applyDefaultColumnPreferences: () => void
  /** The order on screen (a page may adjust the stored one before showing it). */
  columnOrder: string[]
  setColumnOrder: (order: string[]) => void
  saveColumnOrder: (order: string[]) => void
  /** Columns that are always shown, whatever a view says. */
  lockedColumnIds?: string[]
  slices: ListStateSlices
  /**
   * Address-bar keys the page writes itself (e.g. `view` for My Claims). Any
   * other key on arrival is a link carrying its own filters, so the view open
   * last time is not reopened over it.
   */
  urlStateKeys?: string[]
  /**
   * Open the organisation's default view on arrival when the user has no view
   * of their own and none is reopened. `ready` holds it back until the columns
   * it names can be shown.
   */
  openOrgDefault?: { ready: boolean }
}

// Address-bar keys every list writes for paging and sorting (`useTableState`).
const TABLE_STATE_URL_KEYS = ['page', 'page_size', 'ordering']

const ACTIVE_VIEW_KEY_PREFIX = 'raven:table-active-view:v1'
const AUTO_SAVE_DELAY_MS = 600

function activeViewStorageKey(
  tableId: string,
  tenantSlug: string | null,
  userUuid: string | null,
): string | null {
  // Per tenant and per user, so someone signing in after a colleague on the
  // same browser does not land on the colleague's view.
  if (!tenantSlug || !userUuid) return null
  return `${ACTIVE_VIEW_KEY_PREFIX}:${tenantSlug}:${userUuid}:${tableId}`
}

/** A view other people see too: the org default, or one shared with colleagues. */
function seenByOthers(view: TableView): boolean {
  return view.is_org_default || !!view.is_shared || (view.share_scope ?? 'private') !== 'private'
}

function readRememberedView(key: string | null): string | null {
  if (typeof window === 'undefined' || !key) return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeRememberedView(key: string | null, uuid: string | null) {
  if (typeof window === 'undefined' || !key) return
  try {
    if (uuid) window.localStorage.setItem(key, uuid)
    else window.localStorage.removeItem(key)
  } catch {
    // Private mode: the view simply is not reopened next time.
  }
}

/** JSON with object keys sorted, so two equal configs always compare equal. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`
  }
  return JSON.stringify(value ?? null)
}

/**
 * A config with every key this page supports filled in, so a view saved
 * before a key existed compares equal to the same list shown today.
 * Navigation slices are left out: they are not part of what a view changes.
 */
export function normaliseViewConfig(
  config: TableViewConfig,
  slices: ListStateSlices,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    columns: config.columns ?? null,
    columnOrder: config.columnOrder ?? [],
  }
  for (const key of SLICE_KEYS) {
    const slice = slices[key]
    if (!slice || slice.navigation) continue
    out[key] = config[key] ?? slice.empty
  }
  return out
}

export function sameViewConfig(
  a: TableViewConfig,
  b: TableViewConfig,
  slices: ListStateSlices,
): boolean {
  return (
    stableStringify(normaliseViewConfig(a, slices)) ===
    stableStringify(normaliseViewConfig(b, slices))
  )
}

/**
 * What an open view holds if it is saved now: the list as shown, plus the
 * columns the view shows that have not loaded yet (so an early change cannot
 * drop them), with the view's own tab kept as it was saved (a view saved
 * without one stays without one).
 */
export function configForOpenView(
  view: TableViewConfig,
  current: TableViewConfig,
  knownColumnIds: string[],
  slices: ListStateSlices,
): TableViewConfig {
  const known = new Set(knownColumnIds)
  const notLoaded = (view.columns ?? []).filter((id) => !known.has(id))
  const out: TableViewConfig = {
    ...current,
    columns: [...(current.columns ?? []), ...notLoaded],
  }
  for (const key of SLICE_KEYS) {
    if (!slices[key]?.navigation) continue
    const record = out as Record<string, unknown>
    if (view[key] === undefined) delete record[key]
    else record[key] = view[key]
  }
  return out
}

/** Column visibility a view asks for, for the given columns. */
export function visibilityFromView(
  columns: ViewColumn[],
  viewColumns: string[],
  lockedColumnIds: string[] = [],
): Record<string, boolean> {
  const shown = new Set(viewColumns)
  const vis: Record<string, boolean> = {}
  for (const col of columns) vis[col.id] = lockedColumnIds.includes(col.id) || shown.has(col.id)
  return vis
}

type ChipFilter = NonNullable<TableViewConfig['filters']>[number]
type SavedTimeRange = NonNullable<TableViewConfig['timeRange']>

interface ChipColumn {
  header?: unknown
  meta?: { filterField?: string; filterOptions?: Array<{ value: string; label: string }> }
}

/**
 * The per-column filter chips as a slice. A view stores the chip's field,
 * operator and value; the label is rebuilt from the columns when it opens.
 */
export function filterChipsSlice<F extends ChipFilter & { label: string }>(
  activeFilters: F[],
  setActiveFilters: (filters: Array<ChipFilter & { label: string }>) => void,
  columns: ChipColumn[],
): ListStateSlice<ChipFilter[]> {
  return {
    value: activeFilters.map(({ field, operator, value }) => ({ field, operator, value })),
    apply: (filters) =>
      setActiveFilters(
        filters.map((f) => {
          const col = columns.find((c) => c.meta?.filterField === f.field)
          const shown = col?.meta?.filterOptions?.find((o) => o.value === f.value)?.label ?? f.value
          const header = typeof col?.header === 'string' ? col.header : f.field
          return { ...f, label: `${header}: ${shown}` }
        }),
      ),
    empty: [],
  }
}

/** The header's time filter (preset, or a custom from / to range) as a slice. */
export function timeRangeSlice<R extends string>(
  range: R,
  customRange: { from: string; to: string } | undefined,
  setRange: (range: R) => void,
  setCustomRange: (custom: { from: string; to: string } | undefined) => void,
): ListStateSlice<SavedTimeRange> {
  return {
    value: customRange ? { range, ...customRange } : { range },
    apply: ({ range: next, from, to }) => {
      setRange(next as R)
      setCustomRange(from && to ? { from, to } : undefined)
    },
    empty: { range: 'all_time' },
  }
}

/** A time filter change as the slice value `change('timeRange', ...)` takes. */
export function toSavedTimeRange(
  range: string,
  custom?: { from: string; to: string },
): SavedTimeRange {
  return custom ? { range, ...custom } : { range }
}

/**
 * Saved views for a list page: opening, saving, auto-saving and reopening the
 * whole list state - columns, column order, sort and every filter the page
 * has (Filters menu, labels, status, time range, scope). Search text is not
 * part of a view.
 *
 * - Opening a view never writes to it. The user's private view saves itself
 *   after a change. A view others see (the org default, or one the user
 *   shared) saves only through `onSaveActiveView`, which the switcher puts
 *   behind a "this changes it for everyone" confirm. A colleague's shared view
 *   stays as its owner saved it.
 * - Columns that load after a view opened take the visibility the view saved.
 * - The open view is remembered per tenant + table in this browser and
 *   reopened on return, unless the address bar carries its own filters.
 * - With no view open, changes are saved as the user's own preferences, as
 *   before; closing a view brings those back.
 */
export function useSavedListView(options: UseSavedListViewOptions) {
  const {
    tableId,
    columns,
    columnVisibility,
    columnOrder,
    lockedColumnIds,
    slices,
    urlStateKeys,
    openOrgDefault,
  } = options

  const { data: org } = useOrg()
  const tenantSlug = org?.organisation?.tenant_slug ?? null
  const { data: session } = useSession()
  const rememberKey = activeViewStorageKey(tableId, tenantSlug, session?.user?.uuid ?? null)
  const { can } = usePermissions()
  const canManageDefaults = can('table-views.manage-defaults')

  const viewsQuery = useTableViews(tableId)
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data])
  const createView = useCreateTableView()
  const updateView = useUpdateTableView()
  const deleteView = useDeleteTableView()
  const setOrgDefault = useSetOrgDefaultView()

  const [activeViewUuid, setActiveViewUuid] = useState<string | null>(null)
  const activeView = views.find((v) => v.uuid === activeViewUuid) ?? null
  // Bumped on every change the user makes while a view is open; the auto-save
  // runs only once this is non-zero, so opening or loading never writes.
  const [userChanges, setUserChanges] = useState(0)

  // Latest options for callbacks that must not change identity every render.
  const optionsRef = useRef(options)
  optionsRef.current = options
  // Column ids the open view has already set visibility for.
  const appliedColumnIdsRef = useRef<Set<string>>(new Set())

  const canEdit = useCallback(
    (view: TableView) =>
      view.is_org_default ? canManageDefaults : view.is_owner !== false,
    [canManageDefaults],
  )
  // Only the user's private view saves on its own. A view other people see
  // changes only when the user saves it on purpose (onSaveActiveView).
  const autoSaves = useCallback(
    (view: TableView) => canEdit(view) && !seenByOthers(view),
    [canEdit],
  )

  // Rebuilt each render: the pages pass `slices` as a fresh object, so a memo
  // would never hit. It is a few small arrays.
  const currentConfig: TableViewConfig = (() => {
    const locked = lockedColumnIds ?? []
    const config: TableViewConfig = {
      columns: columns
        .filter((col) => {
          if (locked.includes(col.id)) return true
          if (col.id in columnVisibility) return columnVisibility[col.id] !== false
          return col.defaultVisible !== false
        })
        .map((col) => col.id),
      columnOrder,
    }
    for (const key of SLICE_KEYS) {
      const slice = slices[key]
      if (slice) (config as Record<string, unknown>)[key] = slice.value
    }
    return config
  })()
  // What the open view would hold if saved now (see configForOpenView).
  const columnIds = columns.map((c) => c.id)
  const openViewConfig = activeView
    ? configForOpenView(activeView.config, currentConfig, columnIds, slices)
    : null
  const openViewConfigKey = openViewConfig ? stableStringify(openViewConfig) : ''

  const showView = useCallback((view: TableView) => {
    const opts = optionsRef.current
    appliedColumnIdsRef.current = new Set(opts.columns.map((c) => c.id))
    opts.setColumnVisibility(
      view.config.columns
        ? visibilityFromView(opts.columns, view.config.columns, opts.lockedColumnIds)
        : {},
    )
    opts.setColumnOrder(view.config.columnOrder ?? [])
    for (const key of SLICE_KEYS) {
      const slice = opts.slices[key] as ListStateSlice<unknown> | undefined
      if (!slice) continue
      const saved = view.config[key]
      if (saved !== undefined) slice.apply(saved)
      else if (!slice.navigation) slice.apply(slice.empty)
    }
  }, [])

  const showPreferences = useCallback(() => {
    const opts = optionsRef.current
    appliedColumnIdsRef.current = new Set()
    opts.applyDefaultColumnPreferences()
    for (const key of SLICE_KEYS) {
      const slice = opts.slices[key] as ListStateSlice<unknown> | undefined
      if (!slice || slice.navigation) continue
      if (slice.restorePreference) slice.restorePreference()
      else slice.apply(slice.empty)
    }
  }, [])

  // Bumped on every open or close, so a save that finishes after the user
  // picked another view does not take over (see onSaveView).
  const selectionRef = useRef(0)

  const selectView = useCallback(
    (view: TableView | null) => {
      selectionRef.current += 1
      setUserChanges(0)
      setActiveViewUuid(view?.uuid ?? null)
      writeRememberedView(rememberKey, view?.uuid ?? null)
      if (view) showView(view)
      else showPreferences()
    },
    [rememberKey, showView, showPreferences],
  )

  /** The user changed one part of the list (a filter, the sort, a tab). */
  const change = useCallback(
    <K extends ListStateSliceKey>(key: K, value: SliceValue<K>) => {
      const slice = optionsRef.current.slices[key] as ListStateSlice<SliceValue<K>> | undefined
      if (!slice) return
      if (activeView) {
        if (slice.navigation) {
          const pinned = activeView.config[key]
          if (pinned !== undefined && stableStringify(pinned) !== stableStringify(value)) {
            selectView(null)
          }
          slice.apply(value)
          return
        }
        slice.apply(value)
        setUserChanges((n) => n + 1)
        return
      }
      slice.apply(value)
      slice.savePreference?.(value)
    },
    [activeView, selectView],
  )

  const changeColumnVisibility = useCallback(
    (visibility: Record<string, boolean>) => {
      const opts = optionsRef.current
      opts.setColumnVisibility(visibility)
      if (activeView) setUserChanges((n) => n + 1)
      else opts.saveColumnVisibility(visibility)
    },
    [activeView],
  )

  const changeColumnOrder = useCallback(
    (order: string[]) => {
      const opts = optionsRef.current
      opts.setColumnOrder(order)
      if (activeView) setUserChanges((n) => n + 1)
      else opts.saveColumnOrder(order)
    },
    [activeView],
  )

  // Columns that arrive after the view opened (attribute columns, the
  // address column) take the visibility the view saved, without a save.
  const columnIdsKey = columnIds.join('|')
  useEffect(() => {
    if (!activeView?.config.columns) return
    const known = appliedColumnIdsRef.current
    const fresh = optionsRef.current.columns.filter((c) => !known.has(c.id))
    if (fresh.length === 0) return
    const next = { ...optionsRef.current.columnVisibility }
    const fromView = visibilityFromView(
      fresh,
      activeView.config.columns,
      optionsRef.current.lockedColumnIds,
    )
    for (const col of fresh) {
      known.add(col.id)
      next[col.id] = fromView[col.id]
    }
    optionsRef.current.setColumnVisibility(next)
  }, [columnIdsKey, activeView])

  // Auto-save: only after a user change, only to the user's private view, and
  // never twice for the same refused config.
  const pendingSaveRef = useRef<{ uuid: string; config: TableViewConfig } | null>(null)
  const refusedRef = useRef<string | null>(null)
  const saveNow = useCallback(
    (uuid: string, config: TableViewConfig) => {
      const key = `${uuid}:${stableStringify(config)}`
      updateView.mutate(
        { uuid, tableId, config },
        {
          onError: () => {
            refusedRef.current = key
          },
        },
      )
    },
    [tableId, updateView],
  )
  const saveNowRef = useRef(saveNow)
  saveNowRef.current = saveNow

  useEffect(() => {
    pendingSaveRef.current = null
    if (userChanges === 0 || !activeView || !openViewConfig || !autoSaves(activeView)) return
    if (sameViewConfig(activeView.config, openViewConfig, slices)) return
    if (refusedRef.current === `${activeView.uuid}:${openViewConfigKey}`) return
    const pending = { uuid: activeView.uuid, config: openViewConfig }
    pendingSaveRef.current = pending
    const id = setTimeout(() => {
      pendingSaveRef.current = null
      saveNowRef.current(pending.uuid, pending.config)
    }, AUTO_SAVE_DELAY_MS)
    return () => clearTimeout(id)
    // openViewConfigKey stands in for openViewConfig / slices, which change
    // identity on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userChanges, activeView, autoSaves, openViewConfigKey])

  // A change made just before leaving the page is still saved.
  useEffect(
    () => () => {
      const pending = pendingSaveRef.current
      if (pending) saveNowRef.current(pending.uuid, pending.config)
    },
    [],
  )

  // On arrival: reopen the view open last time, unless the address bar
  // carries its own filters or a tab the view was not saved on. Otherwise,
  // optionally land on the organisation default.
  const [searchParams] = useSearchParams()
  const [arrivedWithLinkFilters] = useState(() => {
    const allowed = new Set([...TABLE_STATE_URL_KEYS, ...(urlStateKeys ?? [])])
    return Array.from(searchParams.keys()).some((k) => !allowed.has(k))
  })
  const restoredRef = useRef(false)
  const refetchedRef = useRef(false)
  const orgDefaultDoneRef = useRef(false)
  const { data: viewsData, isFetchedAfterMount, refetch: refetchViews } = viewsQuery
  useEffect(() => {
    if (restoredRef.current || activeViewUuid || !rememberKey) return
    if (!viewsData) return
    const remembered = readRememberedView(rememberKey)
    if (!remembered || arrivedWithLinkFilters) {
      restoredRef.current = true
      return
    }
    const view = views.find((v) => v.uuid === remembered)
    if (!view) {
      // The list kept in the browser may predate the view (saved just before
      // leaving the page): ask the server once, and forget the view only if
      // the server does not have it either.
      if (isFetchedAfterMount) {
        writeRememberedView(rememberKey, null)
        restoredRef.current = true
      } else if (!refetchedRef.current) {
        refetchedRef.current = true
        void refetchViews()
      }
      return
    }
    restoredRef.current = true
    const current = optionsRef.current.slices
    const onAnotherTab = SLICE_KEYS.some((key) => {
      const slice = current[key]
      const pinned = view.config[key]
      return (
        slice?.navigation &&
        pinned !== undefined &&
        stableStringify(pinned) !== stableStringify(slice.value)
      )
    })
    if (onAnotherTab) return
    selectView(view)
  }, [
    activeViewUuid,
    arrivedWithLinkFilters,
    rememberKey,
    selectView,
    views,
    viewsData,
    isFetchedAfterMount,
    refetchViews,
  ])

  const orgDefaultReady = openOrgDefault?.ready ?? false
  useEffect(() => {
    if (!openOrgDefault || orgDefaultDoneRef.current) return
    if (!restoredRef.current || activeViewUuid || !orgDefaultReady) return
    if (!viewsData || arrivedWithLinkFilters) return
    if (views.some((v) => !v.is_org_default)) {
      orgDefaultDoneRef.current = true
      return
    }
    // Latch only once applied, so a cached empty list followed by the real
    // one still lands on the default.
    const orgDefault = views.find((v) => v.is_org_default)
    if (orgDefault) {
      orgDefaultDoneRef.current = true
      selectView(orgDefault)
    }
  }, [
    openOrgDefault,
    orgDefaultReady,
    activeViewUuid,
    arrivedWithLinkFilters,
    selectView,
    views,
    viewsData,
  ])

  const activeViewModified =
    !!activeView &&
    !!openViewConfig &&
    userChanges > 0 &&
    !sameViewConfig(activeView.config, openViewConfig, slices)
  // Changes to a view other people see wait for an explicit save.
  const activeViewNeedsSave =
    activeViewModified && !!activeView && canEdit(activeView) && !autoSaves(activeView)

  const onSaveActiveView = useCallback(() => {
    if (!activeView || !openViewConfig) return
    updateView.mutate(
      { uuid: activeView.uuid, tableId, config: openViewConfig },
      { onError: (err) => toastApiError(err, 'Failed to save view') },
    )
  }, [activeView, openViewConfig, tableId, updateView])

  const onSaveView = useCallback(
    (name: string) => {
      const config = currentConfig
      const selectionAtSave = selectionRef.current
      createView.mutate(
        { table_id: tableId, name, config },
        {
          // The new view is what is on screen: open it, so later changes land
          // in it - unless the user has picked another view meanwhile.
          onSuccess: (view) => {
            if (selectionRef.current !== selectionAtSave) return
            appliedColumnIdsRef.current = new Set(optionsRef.current.columns.map((c) => c.id))
            setUserChanges(0)
            setActiveViewUuid(view.uuid)
            writeRememberedView(rememberKey, view.uuid)
          },
        },
      )
    },
    [createView, currentConfig, rememberKey, tableId],
  )

  const activeViewUuidRef = useRef(activeViewUuid)
  activeViewUuidRef.current = activeViewUuid
  const onDeleteView = useCallback(
    (uuid: string) => {
      deleteView.mutate(
        { uuid, tableId },
        {
          // Close it only once it is really gone; a refused delete leaves it open.
          onSuccess: () => {
            if (activeViewUuidRef.current === uuid) selectView(null)
          },
        },
      )
    },
    [deleteView, selectView, tableId],
  )

  const onRenameView = useCallback(
    (uuid: string, name: string) => updateView.mutate({ uuid, tableId, name }),
    [tableId, updateView],
  )

  // The switcher passes the config of the view whose star was clicked, which
  // need not be the one on screen.
  const onSetOrgDefault = useCallback(
    (config?: TableViewConfig) =>
      setOrgDefault.mutate({
        table_id: tableId,
        name: 'Default',
        config: config ?? currentConfig,
      }),
    [currentConfig, setOrgDefault, tableId],
  )

  return {
    views,
    viewsFetched: viewsQuery.isFetched,
    activeView,
    activeViewUuid,
    activeViewModified,
    currentConfig,
    selectView,
    change,
    changeColumnVisibility,
    changeColumnOrder,
    /** Spread onto `ListPageToolbar`. */
    toolbarProps: {
      views,
      canEditView: canEdit,
      activeViewUuid,
      activeViewModified,
      activeViewNeedsSave,
      onSaveActiveView,
      onSelectView: selectView,
      onSaveView,
      onDeleteView,
      onRenameView,
      onSetOrgDefault,
      currentConfig,
      onColumnVisibilityChange: changeColumnVisibility,
      onColumnOrderChange: changeColumnOrder,
    },
  }
}
